import React, { useCallback, useMemo, useState } from 'react';
import { Activity, BarChart3, DollarSign, FileSpreadsheet, FileText, TrendingUp, UserPlus, Users, Wallet } from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { pick } from '../../utils/moduleHelpers';
import { getThemeColor } from '../../config/appConfig';
import '../../styles/module-shared.css';
import './commission-report.css';
import './client-report.css';

const THEME = getThemeColor('primary');

const pad2 = (n) => String(n).padStart(2, '0');
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 29);
  return { from: toISODate(from), to: toISODate(to) };
};

const num = (v) => Number(v) || 0;
const formatCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num(v));
const formatNumber = (v) => new Intl.NumberFormat('en-US').format(num(v));
const formatLots = (v) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num(v));

// Same payload the Dashboard range summary already returns (Dashboard/DashboardRangeSummary) - no new calculation here.
const parseSummary = (res) => {
  const d = res?.data?.result ?? res?.data ?? {};
  const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
  return {
    fromDate: String(pick(d, 'fromDate', 'FromDate') ?? '').slice(0, 10),
    toDate: String(pick(d, 'toDate', 'ToDate') ?? '').slice(0, 10),
    dataStartDate: String(pick(d, 'dataStartDate', 'DataStartDate') ?? '').slice(0, 10),
    totalClients: num(pick(d, 'totalClients', 'TotalClients')),
    activeClients: num(pick(d, 'activeClients', 'ActiveClients')),
    newClients: num(pick(d, 'newClients', 'NewClients')),
    depositingClients: num(pick(d, 'depositingClients', 'DepositingClients')),
    totalLots: num(pick(d, 'totalLots', 'TotalLots')),
    rebate: num(pick(d, 'rebate', 'Rebate')),
    netDeposit: num(pick(d, 'netDeposit', 'NetDeposit')),
    totalSubIbs: num(pick(d, 'totalSubIbs', 'TotalSubIbs')),
    trend: list(pick(d, 'trend', 'Trend')).map((t) => ({
      label: String(pick(t, 'label', 'Label') ?? ''),
      rebate: num(pick(t, 'rebate', 'Rebate')),
      lots: num(pick(t, 'lots', 'Lots')),
      referrals: num(pick(t, 'referrals', 'Referrals')),
    })),
    subPartners: list(pick(d, 'topSubIbs', 'TopSubIbs')).map((t, i) => ({
      id: pick(t, 'affiliateUserId', 'AffiliateUserId') ?? i,
      code: String(pick(t, 'affiliateCode', 'AffiliateCode') || '—'),
      email: String(pick(t, 'email', 'Email') || '—'),
      isActive: pick(t, 'isActive', 'IsActive') === true,
      newClients: num(pick(t, 'newClients', 'NewClients')),
      lots: num(pick(t, 'lots', 'Lots')),
      deposit: num(pick(t, 'deposit', 'Deposit')),
      withdrawal: num(pick(t, 'withdrawal', 'Withdrawal')),
      net: num(pick(t, 'net', 'Net')),
    })),
  };
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const SUB_HEAD = ['Sub-Partner', 'Email', 'Status', 'New Clients', 'Lots', 'Deposit', 'Withdrawal', 'Net'];

const PerformanceReport = () => {
  const initial = useMemo(defaultRange, []);
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);
  const [applied, setApplied] = useState(initial);
  const [filterError, setFilterError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [effective, setEffective] = useState(initial);

  const fetchData = useCallback(
    async ({ cancelled }) => {
      setLoading(true);
      setError('');
      try {
        const res = await apiService.getDashboardRangeSummary(applied.from, applied.to);
        if (cancelled()) return;
        if (!res?.success) {
          setData(null);
          setError('Unable to load performance data. Please try again.');
          return;
        }
        const parsed = parseSummary(res);
        setData(parsed);
        // The server clamps the range to DashboardSettings:FromDate; show the range it actually used.
        if (parsed.fromDate && parsed.toDate) {
          setFromDate(parsed.fromDate);
          setToDate(parsed.toDate);
          setEffective({ from: parsed.fromDate, to: parsed.toDate });
        }
      } catch {
        if (!cancelled()) {
          setData(null);
          setError('Unable to load performance data. Please try again.');
        }
      } finally {
        if (!cancelled()) setLoading(false);
      }
    },
    [applied]
  );

  useFetchOnceByKey(`performance-report|${applied.from}|${applied.to}`, fetchData, true);

  const dataStart = data?.dataStartDate || '';

  const handleApply = (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) {
      setFilterError('Please select both a start date and an end date.');
      return;
    }
    if (fromDate > toDate) {
      setFilterError('Start date cannot be after end date.');
      return;
    }
    if (dataStart && toDate < dataStart) {
      setFilterError(`Data is available from ${dataStart} onwards.`);
      return;
    }
    setFilterError('');
    setApplied({ from: fromDate, to: toDate });
  };

  const hasActivity =
    !!data &&
    (data.totalClients > 0 ||
      data.totalLots > 0 ||
      data.rebate > 0 ||
      data.newClients > 0 ||
      data.subPartners.length > 0);

  const kpis = data
    ? [
        { label: 'Total Clients', value: formatNumber(data.totalClients), icon: Users, tone: 'blue' },
        { label: 'Active Clients', value: formatNumber(data.activeClients), icon: Activity, tone: 'green' },
        { label: 'New Clients', value: formatNumber(data.newClients), icon: UserPlus, tone: 'violet' },
        { label: 'Depositing Clients', value: formatNumber(data.depositingClients), icon: Wallet, tone: 'amber' },
        { label: 'Trading Volume (Lots)', value: formatLots(data.totalLots), icon: BarChart3, tone: 'violet' },
        { label: 'Commission Earned', value: formatCurrency(data.rebate), icon: DollarSign, tone: 'green' },
        { label: 'Net Deposit', value: formatCurrency(data.netDeposit), icon: TrendingUp, tone: 'blue' },
        { label: 'Sub-Partners', value: formatNumber(data.totalSubIbs), icon: Users, tone: 'amber' },
      ]
    : [];

  // Both files hold exactly what is on screen (own data, effective date range) - nothing is re-fetched or widened.
  const subRows = () =>
    (data?.subPartners || []).map((x) => [
      x.code, x.email, x.isActive ? 'Active' : 'Inactive', x.newClients, x.lots, x.deposit, x.withdrawal, x.net,
    ]);
  const fileStamp = `${effective.from}_${effective.to}`;

  const downloadExcel = async () => {
    if (!data) return;
    setBusy('excel');
    setFilterError('');
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Performance Report');
      ws.addRow(['Performance Report']).font = { bold: true, size: 14 };
      ws.addRow(['Period', `${effective.from} to ${effective.to}`]);
      ws.addRow([]);
      const kh = ws.addRow(['KPI', 'Value']);
      kh.font = { bold: true };
      kpis.forEach((k) => ws.addRow([k.label, k.value]));
      ws.addRow([]);
      const sh = ws.addRow(SUB_HEAD);
      sh.font = { bold: true };
      subRows().forEach((r) => ws.addRow(r));
      ws.columns.forEach((col) => { col.width = 22; });
      const buf = await wb.xlsx.writeBuffer();
      saveBlob(
        new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `Performance_Report_${fileStamp}.xlsx`
      );
    } catch {
      setFilterError('Excel download failed.');
    } finally {
      setBusy('');
    }
  };

  const downloadPdf = () => {
    if (!data) return;
    setBusy('pdf');
    setFilterError('');
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      doc.setFillColor(139, 0, 0);
      doc.rect(0, 0, pageW, 64, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('ABET GLOBAL - PERFORMANCE REPORT', 40, 30);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Period: ${effective.from} to ${effective.to}`, 40, 48);
      autoTable(doc, {
        startY: 84,
        head: [['KPI', 'Value']],
        body: kpis.map((k) => [k.label, k.value]),
        styles: { fontSize: 9, cellPadding: 5, textColor: [40, 40, 40] },
        headStyles: { fillColor: [139, 0, 0], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [253, 247, 247] },
        margin: { left: 40, right: 40, bottom: 40 },
      });
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [SUB_HEAD],
        body: subRows().map((r) => r.map((v, i) => (i >= 4 ? (i === 4 ? formatLots(v) : formatCurrency(v)) : v))),
        styles: { fontSize: 9, cellPadding: 5, textColor: [40, 40, 40] },
        headStyles: { fillColor: [139, 0, 0], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [253, 247, 247] },
        margin: { left: 40, right: 40, bottom: 40 },
      });
      doc.save(`Performance_Report_${fileStamp}.pdf`);
    } catch {
      setFilterError('PDF download failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="page cr-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={TrendingUp}
          title="Performance Report"
          subtitle="View your performance and key business metrics."
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error || filterError} />

        <section className="module-card cr-table-card">
          <form className="cr-filters" onSubmit={handleApply}>
            <div className="cr-field">
              <label htmlFor="pr-from">Start Date</label>
              <input
                id="pr-from"
                type="date"
                value={fromDate}
                min={dataStart || undefined}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="cr-field">
              <label htmlFor="pr-to">End Date</label>
              <input
                id="pr-to"
                type="date"
                value={toDate}
                min={fromDate || dataStart || undefined}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <button type="submit" className="cr-btn" style={{ backgroundColor: THEME }} disabled={loading}>
              Apply
            </button>
            <div className="cl-downloads">
              <button type="button" className="cl-btn cl-btn--outline" onClick={downloadExcel} disabled={loading || !data || busy !== ''}>
                <FileSpreadsheet className="w-4 h-4" />
                {busy === 'excel' ? 'Preparing…' : 'Download Excel'}
              </button>
              <button type="button" className="cl-btn cl-btn--primary" onClick={downloadPdf} disabled={loading || !data || busy !== ''}>
                <FileText className="w-4 h-4" />
                {busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
              </button>
            </div>
          </form>
        </section>

        {loading ? (
          <div className="module-card">
            <AppLoader color={THEME} />
            <p className="cr-empty">Loading performance data...</p>
          </div>
        ) : !data ? null : !hasActivity ? (
          <div className="module-card">
            <p className="cr-empty">No performance data available for the selected period.</p>
          </div>
        ) : (
          <>
            <section className="cr-kpis">
              {kpis.map((kpi) => {
                const Icon = kpi.icon;
                return (
                  <div key={kpi.label} className={`module-card cr-kpi cr-kpi--${kpi.tone}`}>
                    <div className="cr-kpi__icon">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="cr-kpi__label">{kpi.label}</div>
                      <div className="cr-kpi__value">{kpi.value}</div>
                    </div>
                  </div>
                );
              })}
            </section>

            <section className="module-card cr-table-card">
              <div className="cr-trend__head">
                <h2>Sub-Partner Performance</h2>
                <span>{data.subPartners.length} sub-partners</span>
              </div>
              <div className="cr-table-wrap">
                <table className="cr-table">
                  <thead>
                    <tr>
                      <th>Sub-Partner</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>New Clients</th>
                      <th>Lots</th>
                      <th>Deposit</th>
                      <th>Withdrawal</th>
                      <th>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.subPartners.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="cr-empty-cell">
                          No sub-partner performance for the selected period.
                        </td>
                      </tr>
                    ) : (
                      data.subPartners.map((s) => (
                        <tr key={s.id}>
                          <td>{s.code}</td>
                          <td>{s.email}</td>
                          <td>{s.isActive ? 'Active' : 'Inactive'}</td>
                          <td>{formatNumber(s.newClients)}</td>
                          <td>{formatLots(s.lots)}</td>
                          <td>{formatCurrency(s.deposit)}</td>
                          <td>{formatCurrency(s.withdrawal)}</td>
                          <td className="cr-amount">{formatCurrency(s.net)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceReport;
